import { useLocale as useLocaleR42 } from '@/lib/locale';
/**
 * CoachMap — public discovery map for SplashPad coaches.
 *
 * • Renders every active coach with a geocoded lat/lng as a SplashPad-logo
 *   marker on an OpenStreetMap base layer.
 * • City search uses Nominatim (OSM's free geocoder) — coach types "Atlanta"
 *   and the map flies there.
 * • Coaches in the same city cluster into one marker labelled "N coaches in
 *   <City>" — clicking expands the cluster into a list popup.
 * • Marker click → opens a popover with coach name + service area + a
 *   "Visit page" button that deep-links to /coach/{slug}.
 *
 * Privacy guarantee: this component never receives or renders a coach's
 * street-level coordinates. Backend resolves to city centroid only.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Search, MapPin, Loader2, Sparkles, Globe2, ArrowRight, Navigation } from 'lucide-react';
import api from '@/lib/api';

const DEFAULT_CENTER = [39.8283, -98.5795]; // geographic center of the US
const DEFAULT_ZOOM = 4;

/** Group coaches by their (rounded) lat/lng so multiple coaches in the same
 *  city collapse into a single cluster marker. We round to 2 decimals which
 *  is roughly a 1km grid — good enough to merge coaches in the same city
 *  without merging coaches in neighbouring towns. */
function clusterCoaches(coaches) {
    const buckets = new Map();
    for (const c of coaches) {
        if (typeof c.lat !== 'number' || typeof c.lng !== 'number') continue;
        const key = `${c.lat.toFixed(2)}|${c.lng.toFixed(2)}`;
        if (!buckets.has(key)) {
            buckets.set(key, { lat: c.lat, lng: c.lng, city: c.city, state: c.state, coaches: [] });
        }
        buckets.get(key).coaches.push(c);
    }
    return Array.from(buckets.values());
}

/** Build a Leaflet divIcon styled with the SplashPad logo. We use divIcon
 *  rather than L.icon because it lets us layer the logo image inside a
 *  branded pill (with a count badge for clusters) using normal CSS. */
function buildIcon({ count = 1, accent = '#00d4ff' }) {
    // 44px square pill, white background, splashpad-logo png inside.
    // The "tail" gives the marker its classic pin shape.
    const html = `
        <div class="splashpad-pin" style="--pin-accent: ${accent};">
            <img src="/splashpad-logo.png" alt="" />
            ${count > 1 ? `<span class="splashpad-pin__count">${count}</span>` : ''}
        </div>
    `;
    return L.divIcon({
        html,
        className: 'splashpad-pin-wrapper',
        iconSize: [44, 52],
        iconAnchor: [22, 48], // tip of the tail
        popupAnchor: [0, -44],
    });
}

/** Imperatively pan/zoom the map. Used by the city-search and "reset" buttons.
 *  Must live inside <MapContainer> because useMap() reads from its context. */
function MapController({ target }) {
    const map = useMap();
    useEffect(() => {
        if (!target) return;
        map.flyTo([target.lat, target.lng], target.zoom || 11, { duration: 0.9 });
    }, [target, map]);
    return null;
}

export default function CoachMap() {
    const { t: t42 } = useLocaleR42();
    const [coaches, setCoaches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [searching, setSearching] = useState(false);
    const [target, setTarget] = useState(null);
    const [foundCount, setFoundCount] = useState(null); // result of last search
    const inputRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        api.get('/public/coaches/map')
            .then((r) => { if (!cancelled) setCoaches(r.data || []); })
            .catch(() => { if (!cancelled) setError("Couldn't load the coach map. Refresh to try again."); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, []);

    const clusters = useMemo(() => clusterCoaches(coaches), [coaches]);

    /** City search via Nominatim — purely client-side, no backend round-trip.
     *  Nominatim allows browser usage with a descriptive User-Agent which we
     *  can't fully set from JS (fetch strips it), but they accept the request
     *  if the Referer is present. We're well under their usage limits. */
    const handleSearch = useCallback(async (e) => {
        e?.preventDefault?.();
        const q = search.trim();
        if (!q) return;
        setSearching(true);
        setError('');
        try {
            const r = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}&format=json&limit=1&addressdetails=1`);
            const data = await r.json();
            if (!data || !data.length) {
                setError(`No place found matching "${q}". Try a city + state, e.g. "Atlanta, GA".`);
                setFoundCount(null);
                return;
            }
            const top = data[0];
            const lat = parseFloat(top.lat);
            const lng = parseFloat(top.lon);
            setTarget({ lat, lng, zoom: 10 });
            // count coaches within ~50 miles (0.7° latitude ≈ 78km)
            const near = coaches.filter((c) => {
                const dLat = Math.abs(c.lat - lat);
                const dLng = Math.abs(c.lng - lng);
                return dLat < 0.7 && dLng < 0.9;
            }).length;
            setFoundCount(near);
        } catch (err) {
            setError('Could not search right now. Check your connection and try again.');
        } finally {
            setSearching(false);
        }
    }, [search, coaches]);

    const recenter = () => {
        setTarget({ lat: DEFAULT_CENTER[0], lng: DEFAULT_CENTER[1], zoom: DEFAULT_ZOOM });
        setFoundCount(null);
        setSearch('');
    };

    return (
        <div className="rounded-3xl border border-[#1a3a5c]/10 bg-white shadow-[0_24px_60px_-30px_rgba(10,22,40,0.35)] overflow-hidden" data-testid="coach-discovery-map">
            {/* Header — search bar + result count + reset */}
            <div className="flex flex-col gap-3 border-b border-[#1a3a5c]/10 bg-gradient-to-br from-[#e6fbff] via-white to-[#fff7e6] px-5 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-6">
                <div className="min-w-0">
                    <div className="inline-flex items-center gap-1.5 rounded-full bg-[#0a1628] px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white">
                        <Sparkles className="h-3 w-3 text-[#ffd700]" /> {t42("SplashPad coaches")} </div>
                    <h3 className="mt-2 font-display text-xl font-bold text-[#0a1628] sm:text-2xl"> {t42("Find a SplashPad coach near you")} </h3>
                    <p className="text-sm text-[#1a3a5c]/80">
                        {loading
                            ? t42("Loading coaches…")
                            : <>
                                <strong>{coaches.length}</strong> {t42("verified instructor")}{coaches.length === 1 ? '' : 's'} {t42("on the platform.")} {foundCount !== null && (
                                    <> · <strong>{foundCount}</strong> {t42("within ~50 miles of your search.")}</>
                                )}
                            </>}
                    </p>
                </div>
                <form onSubmit={handleSearch} className="flex w-full max-w-md items-center gap-2 sm:w-auto" role="search">
                    <div className="relative flex-1">
                        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#1a3a5c]/50" />
                        <Input
                            ref={inputRef}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder={t42("Atlanta, GA")}
                            aria-label={t42("Search a city, town, or zip code")}
                            className="h-11 rounded-xl border-[#1a3a5c]/15 pl-9"
                            data-testid="coach-map-search-input"
                        />
                    </div>
                    <Button type="submit" disabled={searching || !search.trim()} className="sd-cta sd-focus h-11 rounded-xl px-4" data-testid="coach-map-search-button">
                        {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : t42("Search")}
                    </Button>
                    {(target || foundCount !== null) && (
                        <Button type="button" variant="ghost" onClick={recenter} className="h-11 rounded-xl px-3 text-[#1a3a5c]" data-testid="coach-map-reset-button" aria-label={t42("Reset map view")}>
                            <Navigation className="h-4 w-4" />
                        </Button>
                    )}
                </form>
            </div>

            {/* Error banner */}
            {error && (
                <div className="border-b border-[#ffd700]/30 bg-[#fff8d6] px-5 py-2.5 text-sm text-[#0a1628]" role="alert" data-testid="coach-map-error">
                    {error}
                </div>
            )}

            {/* Map / empty state */}
            <div className="relative" style={{ height: 480 }}>
                {!loading && coaches.length === 0 ? (
                    <div className="flex h-full flex-col items-center justify-center gap-3 bg-[#f4f7fb] p-8 text-center" data-testid="coach-map-empty">
                        <Globe2 className="h-10 w-10 text-[#1a3a5c]/40" />
                        <h4 className="font-display text-lg font-bold text-[#0a1628]">{t42("No coaches on the map yet")}</h4>
                        <p className="max-w-md text-sm text-[#1a3a5c]/80"> {t42("Be the first swim instructor in your area to join SplashPad — your city will show up here so families nearby can find you.")} </p>
                        <Button asChild className="sd-cta sd-focus mt-2 h-11 rounded-xl">
                            <Link to="/signup">{t42("Become a SplashPad coach")} <ArrowRight className="ml-1 h-4 w-4" /></Link>
                        </Button>
                    </div>
                ) : (
                    <MapContainer
                        center={DEFAULT_CENTER}
                        zoom={DEFAULT_ZOOM}
                        scrollWheelZoom={false}
                        worldCopyJump
                        className="h-full w-full"
                        attributionControl
                    >
                        <TileLayer
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        />
                        <MapController target={target} />
                        {clusters.map((cl) => (
                            <Marker
                                key={`${cl.lat}|${cl.lng}`}
                                position={[cl.lat, cl.lng]}
                                icon={buildIcon({ count: cl.coaches.length, accent: cl.coaches[0]?.primary_color || '#00d4ff' })}
                            >
                                <Popup>
                                    <div className="min-w-[220px]" data-testid={`coach-map-popup-${cl.lat}-${cl.lng}`}>
                                        <div className="flex items-center gap-2 border-b border-[#1a3a5c]/10 pb-2">
                                            <MapPin className="h-4 w-4 text-[#00d4ff]" />
                                            <div className="font-display text-sm font-bold text-[#0a1628]">
                                                {cl.city ? `${cl.city}${cl.state ? `, ${cl.state}` : ''}` : t42("SplashPad coach")}
                                            </div>
                                        </div>
                                        <div className="mt-2 space-y-2">
                                            {cl.coaches.map((c) => (
                                                <div key={c.id} className="flex items-center justify-between gap-2">
                                                    <div className="min-w-0 flex-1">
                                                        <div className="truncate text-sm font-semibold text-[#0a1628]">{c.display_name}</div>
                                                        <div className="truncate text-xs text-[#1a3a5c]/70">
                                                            {c.service_area || c.pool_name || `${c.city || ''}${c.state ? `, ${c.state}` : ''}`}
                                                        </div>
                                                    </div>
                                                    {c.plan === 'pro' && (
                                                        <Badge className="shrink-0 bg-[#ffd700]/30 text-[#0a1628] hover:bg-[#ffd700]/40">{t42("Pro")}</Badge>
                                                    )}
                                                    <Link
                                                        to={`/coach/${c.slug}`}
                                                        className="shrink-0 rounded-lg bg-[#0a1628] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#1a3a5c]"
                                                        data-testid={`coach-map-visit-${c.slug}`}
                                                    > {t42("Visit")} </Link>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </Popup>
                            </Marker>
                        ))}
                    </MapContainer>
                )}
                {loading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/60 backdrop-blur-sm" data-testid="coach-map-loading">
                        <Loader2 className="h-8 w-8 animate-spin text-[#00d4ff]" />
                    </div>
                )}
            </div>
        </div>
    );
}
